import React from 'react';
import { X, Layout, Volume2, VolumeX, Monitor, Smartphone, Keyboard } from 'lucide-react';
import type { VideoDisplayOptions } from '../types';

interface MenuViewProps {
  onClose: () => void;
  displayOptions: VideoDisplayOptions;
  onDisplayOptionsChange: (options: Partial<VideoDisplayOptions>) => void;
}

export function MenuView({ onClose, displayOptions, onDisplayOptionsChange }: MenuViewProps) {
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = React.useState(displayOptions.isMuted ?? true);

  React.useEffect(() => {
    setIsMuted(displayOptions.isMuted ?? true);
  }, [displayOptions.isMuted]);

  React.useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [onClose]);

  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    onDisplayOptionsChange({
      isMuted: newMuted
    });
  };

  const toggleCinemaMode = () => {
    onDisplayOptionsChange({
      centered: !displayOptions.centered
    });
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm">
      <div
        ref={menuRef}
        className="absolute left-0 top-0 bottom-0 w-56 bg-black/95 backdrop-blur-sm border-r border-gray-800 animate-[slideIn_0.3s_ease-out] safe-area-top safe-area-bottom"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <span className="text-white font-bold text-xl font-poppins">Menu</span>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800/50 rounded-full"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        
        <nav className="p-2">
          <div className="px-4 py-3">
            <h3 className="text-gray-400 text-sm font-medium mb-3">Video Display</h3>
            <button
              onClick={toggleCinemaMode}
              className="w-full flex items-center justify-between bg-gray-800/50 rounded-lg px-4 py-3 text-white hover:bg-gray-800/70 transition-colors mb-3"
            >
              <div className="flex items-center gap-3">
                <Layout className={`w-5 h-5 ${displayOptions.centered ? 'text-blue-500' : ''}`} />
                <span>Cinema Mode</span>
              </div>
              <div className={`w-10 h-6 rounded-full transition-colors ${
                displayOptions.centered ? 'bg-blue-600' : 'bg-gray-700'
              } relative`}>
                <div className={`absolute w-5 h-5 rounded-full bg-white top-0.5 transition-transform ${
                  displayOptions.centered ? 'translate-x-4' : 'translate-x-0.5'
                }`} />
              </div>
            </button>
            
            <button
              onClick={toggleMute}
              className="w-full flex items-center justify-between bg-gray-800/50 rounded-lg px-4 py-3 text-white hover:bg-gray-800/70 transition-colors"
            >
              <div className="flex items-center gap-3">
                {isMuted ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5 text-blue-500" />
                )}
                <span>Sound</span>
              </div>
              <div className={`w-10 h-6 rounded-full transition-colors ${
                !isMuted ? 'bg-blue-600' : 'bg-gray-700'
              } relative`}>
                <div className={`absolute w-5 h-5 rounded-full bg-white top-0.5 transition-transform ${
                  !isMuted ? 'translate-x-4' : 'translate-x-0.5'
                }`} />
              </div>
            </button>
          </div>
          
          <div className="px-4 py-2 mt-2">
            <h3 className="text-gray-400 text-sm font-medium mb-3">Desktop Shortcuts</h3>
            <div className="bg-gray-800/30 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Keyboard className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-300">Keyboard Controls</span>
                </div>
              </div>
              <div className="text-gray-400 text-xs space-y-2">
                <div className="flex items-center justify-between">
                  <span>Navigate videos</span>
                  <span><kbd>↑/↓</kbd> or <kbd>j/k</kbd></span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Toggle mute</span>
                  <span><kbd>m</kbd></span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Toggle cinema mode</span>
                  <span><kbd>f</kbd></span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Switch tabs</span>
                  <span><kbd>1-5</kbd></span>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
}